import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IOS_APP_STORE_ID, PLAY_STORE_URL } from '../../config/app-store';
import { useAuthStore } from '../../stores/auth.store';
import LinkButton from './components/LinkButton';
import { faqs } from './data/faq-data';
import AppShowcaseSection from './sections/AppShowcaseSection';
import BlogPreviewSection from './sections/BlogPreviewSection';
import FAQSection from './sections/FAQSection';
import FeaturesSection from './sections/FeaturesSection';
import FinalCTASection from './sections/FinalCTASection';
import LandingFooter from './sections/Footer';
import HeroSection from './sections/HeroSection';
import HowItWorksSection from './sections/HowItWorksSection';
import LandingNavbar from './sections/Navbar';
import PainPointsSection from './sections/PainPointsSection';
import PricingSection from './sections/PricingSection';
import QuizCTASection from './sections/QuizCTASection';
import SocialProofBar from './sections/SocialProofBar';
import WhereItHelpsSection from './sections/WhereItHelpsSection';
import WhoThisIsForSection from './sections/WhoThisIsForSection';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const softwareAppSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'EmpathEase',
  applicationCategory: 'EducationApplication',
  operatingSystem: 'iOS, Android, Web',
  description:
    'Master Nonviolent Communication through practice. On-demand empathy and a complete NVC course to help you connect with yourself and others.',
  image: 'https://empathease.app/logo.webp',
  screenshot: [
    'https://empathease.app/screenshots/empathease-nvc-course-learning-path.webp',
    'https://empathease.app/screenshots/empathease-live-empathy-sessions.webp',
    'https://empathease.app/screenshots/empathease-giraffe-translator-tool.webp',
    'https://empathease.app/screenshots/empathease-ai-tools-overview.webp',
  ],
  installUrl: PLAY_STORE_URL,
  downloadUrl: PLAY_STORE_URL,
  author: {
    '@type': 'Organization',
    name: 'EmpathEase',
    url: 'https://empathease.app',
  },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
  // aggregateRating intentionally omitted until we have legitimate ratings (Google policy).
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'EmpathEase',
  url: 'https://empathease.app',
  logo: 'https://empathease.app/logo.webp',
  sameAs: [
    'https://instagram.com/empatheaseapp',
    'https://tiktok.com/@empy871',
    'https://www.youtube.com/@EmpatheaseTalks',
    'https://www.facebook.com/empathease.talks/',
    'https://www.linkedin.com/in/empathease-talks-126933359',
    'https://www.reddit.com/r/empathease/',
  ],
};

/**
 * Landing Page Component
 * Marketing page for non-authenticated web visitors
 */
export default function LandingPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const scrollTo = searchParams.get('scrollTo');
    if (scrollTo) {
      // Small delay to ensure sections are rendered
      requestAnimationFrame(() => {
        const el = document.getElementById(scrollTo);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
        // Clean up the query param
        setSearchParams({}, { replace: true });
      });
    }
  }, [searchParams, setSearchParams]);

  return (
    // Redesign PR10 (2026-10-20): the page opts into the design-system scope
    // (.ee-page) on its own root. Styles: design-system/pages/landing.css.
    // Prerendered for crawlers: headings, links, copy and structured data are
    // pinned by ./__tests__/landing-redesign.test.tsx.
    <div className="ee-page ee-landing">
      {/* React 19 auto-hoists title, meta, and link tags to <head> */}
      <title>EmpathEase - NVC Communication App for Couples &amp; Relationships</title>
      <meta
        name="description"
        content="Master Nonviolent Communication through practice. On-demand empathy and a complete NVC course to help you connect with yourself and others."
      />
      <link rel="canonical" href="https://empathease.app/" />
      {/* iOS Safari Smart App Banner — dormant until IOS_APP_STORE_ID is set (post-launch). */}
      {IOS_APP_STORE_ID ? (
        <meta name="apple-itunes-app" content={`app-id=${IOS_APP_STORE_ID}`} />
      ) : null}
      <link
        rel="preload"
        as="image"
        type="image/webp"
        href="/screenshots/empathease-stats-empathy-impact.webp"
        media="(min-width: 768px)"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <LandingNavbar look="ee" />
      <main>
        <HeroSection />
        <SocialProofBar />
        <PainPointsSection />
        <HowItWorksSection />
        <WhoThisIsForSection />
        <WhereItHelpsSection />
        <FeaturesSection />
        <AppShowcaseSection />
        <BlogPreviewSection />
        <PricingSection />
        <FAQSection />
        <QuizCTASection />
        <FinalCTASection />
      </main>
      <LandingFooter />

      {/* Sticky mobile CTA */}
      <div className="ee-landing-sticky">
        <LinkButton to={isAuthenticated ? '/app' : '/register'} size="lg" fullWidth>
          {isAuthenticated ? 'Go to App' : 'Get Started — No Credit Card Needed'}
        </LinkButton>
      </div>
    </div>
  );
}
